import { COLOR_VARS } from 'src/utils/theme/consts';
import { ColorVar } from 'src/utils/theme/types';

interface GetColorVarParams {
	as?: 'variable' | 'cssFn';
}

function hexToRgba(hex: string): string {
	if (!/^#[0-9A-Fa-f]{8}$/.test(hex)) {
		return hex;
	}

	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	const a = parseInt(hex.slice(7, 9), 16) / 255;

	return `rgba(${r}, ${g}, ${b}, ${a.toFixed(2)})`;
}

/**
 * A utility function to get the theme color variable string given a valid color variable name.
 * ex. `getColorVar('text-default')` => `'var(--color-text-default)'` if as is `cssFn`
 * or `getColorVar('text-default')` => `'--color-text-default'` if `as` is `variable`
 */
export function getColorVar(
	colorVarName: ColorVar,
	params?: GetColorVarParams
) {
	const colorVarValue = COLOR_VARS[colorVarName];

	return params?.as === 'variable' ? colorVarValue : `var(${colorVarValue})`;
}

/**
 * A utility function to get the theme color value given a valid CSS variable.
 * This is useful when an element does not have access to the root variables,
 * typically the case for 3rd party widgets loaded in iframes (e.g. TradingView).
 */
export function getResolvedColorValue(colorVarName: ColorVar) {
	if (typeof document === 'undefined') {
		return '';
	}

	const varName = getColorVar(colorVarName, { as: 'variable' });
	const tailwindStyles = getComputedStyle(document.documentElement);

	const colorValue = tailwindStyles.getPropertyValue(varName).trim();

	return hexToRgba(colorValue);
}

/**
 * Scales the alpha channel of a resolved color (rgba(...) or #rrggbb) by
 * `alphaMultiplier`. Useful for 3rd party widgets (e.g. TradingView) that
 * need a lower-opacity variant of an existing color token.
 */
export function withAlpha(color: string, alphaMultiplier: number): string {
	const rgbaMatch = color.match(
		/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)$/
	);
	if (rgbaMatch) {
		const [, r, g, b, a] = rgbaMatch;
		const alpha = (a !== undefined ? parseFloat(a) : 1) * alphaMultiplier;
		return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(2)})`;
	}

	const hexMatch = color.match(/^#([0-9A-Fa-f]{6})$/);
	if (hexMatch) {
		const r = parseInt(hexMatch[1].slice(0, 2), 16);
		const g = parseInt(hexMatch[1].slice(2, 4), 16);
		const b = parseInt(hexMatch[1].slice(4, 6), 16);
		return `rgba(${r}, ${g}, ${b}, ${alphaMultiplier.toFixed(2)})`;
	}

	return color;
}
